<template>
  <HeadlessDisclosure
    v-slot="{ open }"
    as="nav"
    class="z-1 bg-background shadow-lg shadow-black/10"
  >
    <div class="max-w-10xl mx-auto px-4 sm:px-6 xl:px-8">
      <!--
        items-stretch, not items-center: the active tab's underline is a
        bottom border on the link itself, so the link has to run the full
        height of the bar or the rule floats above the edge.
      -->
      <div class="flex min-h-16 items-stretch">
        <div class="flex min-w-0 flex-1 items-stretch">
          <div class="nav:hidden -mr-2 flex items-center">
            <!-- Mobile menu button -->
            <HeadlessDisclosureButton
              class="text-background-fg/50 hover:bg-background-fg/5 inline-flex items-center justify-center rounded-md bg-white p-2 hover:text-opacity-60"
            >
              <span class="sr-only">Open main menu</span>
              <IconHeroiconsSolidMenu
                v-if="!open"
                class="block h-6 w-6"
                aria-hidden="true"
              />
              <IconHeroiconsSolidX
                v-else
                class="block h-6 w-6"
                aria-hidden="true"
              />
            </HeadlessDisclosureButton>
          </div>
          <div class="ml-4 flex flex-shrink-0 items-center">
            <RouterLink
              :to="{ name: 'home' }"
              class="my-3 inline-flex items-center"
            >
              <IconCamcCamcLogo class="hidden h-10 w-auto sm:block" />

              <IconCamcCamcLogo class="h-8 w-auto sm:hidden" />
            </RouterLink>
          </div>

          <div class="nav:flex ml-6 hidden items-stretch space-x-2">
            <template v-for="route in routes" :key="route.label">
              <TurboRouterLink
                v-slot="{ href, onClick, isActive, component, disabled }"
                :to="route.to"
              >
                <Component
                  :is="component"
                  :disabled="disabled"
                  :href="href"
                  :class="[
                    'disabled:border-transparent disabled:opacity-40',
                    isActive
                      ? 'border-primary'
                      : 'text-background-fg/40 hover:border-background-fg/50 border-transparent hover:text-opacity-50',
                    'border-b-3 inline-flex items-center whitespace-nowrap px-3 pt-1 text-sm font-medium',
                  ]"
                  :aria-current="isActive ? 'page' : undefined"
                  @click="onClick"
                >
                  <Component :is="route.icon" class="mr-2" />
                  {{ route.label }}
                </Component>
              </TurboRouterLink>
            </template>
          </div>
        </div>

        <div
          class="nav:ml-6 nav:flex nav:items-center hidden flex-none space-x-2"
        >
          <!-- A storage compound has gate cameras and kiosks to watch too. -->
          <CameraStreamPopover v-if="isDigitalArrivals || hasStorageCompound" />
          <HeaderAlerts />
          <HeaderSiteDropdown class="w-48" />
          <HeadlessMenu as="div" class="relative">
            <div class="flex w-48 justify-end">
              <HeadlessMenuButton
                class="text-background-fg/60 flex w-48 items-center justify-between rounded-full px-3 py-2 text-base"
              >
                <span class="sr-only">Open user menu</span>
                <span class="line-clamp-1">
                  {{ currentUser?.email }}
                </span>
                <IconHeroiconsSolidChevronDown class="ml-1 h-6 w-6" />
              </HeadlessMenuButton>
            </div>
            <Transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <HeadlessMenuItems
                class="ring-background-fg bg-background absolute right-0 mt-2 w-48 origin-top-right rounded-md py-1 shadow-lg ring-1 ring-opacity-5 focus:outline-none"
              >
                <template v-for="route in secondaryRoutes" :key="route.label">
                  <TurboRouterLink
                    v-slot="{ href, onClick, isActive, component, disabled }"
                    :to="route.to"
                  >
                    <HeadlessMenuItem
                      :as="component"
                      :disabled="disabled"
                      :href="href"
                      :class="[
                        'disabled:opacity-40',
                        isActive
                          ? 'text-primary'
                          : 'text-background-fg/70 hover:bg-background-fg/10',
                        menuRowClass,
                      ]"
                      :aria-current="isActive ? 'page' : undefined"
                      @click="onClick"
                    >
                      <Component :is="route.icon" :class="menuIconClass" />
                      {{ route.label }}
                    </HeadlessMenuItem>
                  </TurboRouterLink>
                </template>
                <div
                  v-if="secondaryRoutes.length"
                  class="border-background-fg/10 my-1 border-t"
                />
                <template v-if="currentUser?.isSuperuser">
                  <HeadlessMenuItem>
                    <a
                      href="/admin/"
                      target="_blank"
                      :class="[
                        'text-background-fg/70 hover:bg-background-fg/10',
                        menuRowClass,
                      ]"
                    >
                      Admin
                    </a>
                  </HeadlessMenuItem>
                  <HeadlessMenuItem>
                    <a
                      href="/companion/"
                      target="_blank"
                      :class="[
                        'text-background-fg/70 hover:bg-background-fg/10',
                        menuRowClass,
                      ]"
                    >
                      Arrival Companion
                    </a>
                  </HeadlessMenuItem>
                </template>
                <HeadlessMenuItem>
                  <button
                    :class="['text-error hover:bg-error/10', menuRowClass]"
                    @click="logOut"
                  >
                    Log Out
                  </button>
                </HeadlessMenuItem>
              </HeadlessMenuItems>
            </Transition>
          </HeadlessMenu>

          <RouterLink
            :to="{ name: 'bookingCreate' }"
            class="bg-primary text-primary-fg rounded-full px-3 py-2 text-center text-sm"
          >
            Create Booking
          </RouterLink>
        </div>
        <div
          class="nav:hidden my-auto flex flex-none items-center space-x-2 px-2"
        >
          <RouterLink
            v-if="isDigitalArrivals"
            :to="{ name: 'cameraSign' }"
            class="text-background-fg/50 relative flex flex-shrink-0 items-center justify-center rounded-full bg-white p-1 hover:text-opacity-80 focus:outline-none"
          >
            <span class="sr-only">View cameras</span>
            <IconHeroiconsSolidVideoCamera class="h-6 w-6" aria-hidden="true" />
          </RouterLink>
          <HeaderAlerts />
        </div>
      </div>
    </div>
    <!--
      The panel reads top to bottom as: where am I, what do I want to do,
      where can I go, who am I. The site selector sat between the two link
      lists before, which split navigation in half around a control that
      isn't navigation at all — it belongs with the context it sets.
    -->
    <HeadlessDisclosurePanel
      class="border-background-fg/10 nav:hidden border-t"
    >
      <HeaderSiteDropdown class="w-full" underlined />
      <div class="px-4 py-4">
        <TurboRouterLink
          v-slot="{ href, onClick, component, disabled }"
          :to="{ name: 'bookingCreate' }"
        >
          <HeadlessDisclosureButton
            :as="component"
            :disabled="disabled"
            :href="href"
            class="bg-primary text-primary-fg block w-full rounded-full px-4 py-2.5 text-center text-sm font-medium disabled:opacity-40"
            @click="onClick"
          >
            Create Booking
          </HeadlessDisclosureButton>
        </TurboRouterLink>
      </div>

      <div class="border-background-fg/10 space-y-1 border-t py-2">
        <template v-for="route in routes" :key="route.label">
          <TurboRouterLink
            v-slot="{ href, onClick, isActive, component, disabled }"
            :to="route.to"
          >
            <HeadlessDisclosureButton
              :as="component"
              :disabled="disabled"
              :href="href"
              :class="[
                'disabled:border-none disabled:opacity-40',
                isActive
                  ? 'text-primary border-primary bg-primary/10'
                  : 'text-background-fg/60 hover:border-background-fg/30 hover:bg-background-fg/10 hover:text-background-fg/80 border-transparent',
                navRowClass,
              ]"
              :aria-current="isActive ? 'page' : undefined"
              @click="onClick"
            >
              <Component :is="route.icon" :class="navIconClass" />
              {{ route.label }}
            </HeadlessDisclosureButton>
          </TurboRouterLink>
        </template>
      </div>

      <div class="border-background-fg/10 space-y-1 border-t py-2">
        <template v-for="route in secondaryRoutes" :key="route.label">
          <TurboRouterLink
            v-slot="{ href, onClick, isActive, component, disabled }"
            :to="route.to"
          >
            <HeadlessDisclosureButton
              :as="component"
              :disabled="disabled"
              :href="href"
              :class="[
                'disabled:border-none disabled:opacity-40',
                isActive
                  ? 'text-primary border-primary bg-primary/10'
                  : 'text-background-fg/60 hover:border-background-fg/30 hover:bg-background-fg/10 hover:text-background-fg/80 border-transparent',
                navRowClass,
              ]"
              :aria-current="isActive ? 'page' : undefined"
              @click="onClick"
            >
              <Component :is="route.icon" :class="navIconClass" />
              {{ route.label }}
            </HeadlessDisclosureButton>
          </TurboRouterLink>
        </template>
        <template v-if="currentUser?.isSuperuser">
          <a
            href="/admin/"
            target="_blank"
            :class="[
              'text-background-fg/60 hover:bg-background-fg/10 hover:text-background-fg/80 border-transparent',
              navRowClass,
              'pl-11',
            ]"
          >
            Admin
          </a>
          <a
            href="/companion/"
            target="_blank"
            :class="[
              'text-background-fg/60 hover:bg-background-fg/10 hover:text-background-fg/80 border-transparent',
              navRowClass,
              'pl-11',
            ]"
          >
            Arrival Companion
          </a>
        </template>
        <HeadlessDisclosureButton
          as="button"
          :class="[
            'text-error hover:bg-error/10 border-transparent',
            navRowClass,
            'pl-11',
          ]"
          @click="logOut"
        >
          Log Out
        </HeadlessDisclosureButton>
      </div>
    </HeadlessDisclosurePanel>
  </HeadlessDisclosure>
</template>

<script setup lang="ts">
import { TurboRouterLink } from "@/components/util/TurboRouterLink";
import IconHeroiconsSolidChartBar from "~icons/heroicons-solid/chart-bar";
import IconHeroiconsSolidDeviceMobile from "~icons/heroicons-solid/device-phone-mobile";
import IconHeroiconsSolidFingerPrint from "~icons/heroicons-solid/finger-print";
import IconHeroiconsSolidIdentification from "~icons/heroicons-solid/identification";
import IconHeroiconsSolidKey from "~icons/heroicons-solid/key";
import IconHeroiconsSolidVideoCamera from "~icons/heroicons-solid/video-camera";
import IconHeroiconsSolidMap from "~icons/heroicons-solid/map";
import IconHeroiconsSolidArchiveBox from "~icons/heroicons-solid/archive-box";

const menuRowClass =
  "flex w-full items-center px-4 py-2 text-left text-sm font-medium";
const menuIconClass = "mr-2.5 h-4 w-4 flex-shrink-0";

// Every row in the mobile panel — nav link, account link, log out — shares a
// shape so the icons line up in one column down the whole sheet. The
// transparent left border is what reserves room for the active marker.
const navRowClass =
  "flex w-full items-center border-l-4 py-2.5 pl-3 pr-4 text-left text-base font-medium";
const navIconClass = "mr-3 h-5 w-5 flex-shrink-0";

const { currentUser } = useCurrentUser();
const { logOut } = useAuth();
const isDigitalArrivals = useIsDigitalArrivals();
const isKeypad = useIsKeypad();
const isExperienceFreedom = useIsExperienceFreedom();
const hasKeysafePitch = useHasKeysafePitch();
const isCompanion = useIsCompanionSiteSelected();
const hasStorageCompound = useHasStorageCompound();

// The bar carries the pages people live in day to day. Everything else is a
// place you go occasionally, so it sits in the user menu (and, on mobile, in
// the account section of the disclosure panel).
//
// Allow List and Keycodes are in the bar at CAMC's request: site staff treat
// both as everyday work, not an occasional trip. Each is gated on the site
// having the feature, so the bar runs from four links up to six (seven with
// Storage, on a site with a storage compound).
const routes = computed(() => [
  {
    label: "Bookings",
    to: { name: "bookings" },
    icon: IconHeroiconsSolidIdentification,
  },
  // Entry Log lands on the ANPR tab; keypad-only sites get bounced to the
  // keypad tab by the page's own redirect watcher.
  ...(isDigitalArrivals.value || isKeypad.value
    ? [
        {
          label: "Entry Log",
          to: { name: "cameraEvents" },
          icon: IconHeroiconsSolidVideoCamera,
        },
        {
          label: "Allow List",
          to: { name: "allowlist" },
          icon: IconHeroiconsSolidKey,
        },
      ]
    : []),
  {
    label: "Site Plan",
    to: { name: "pitches" },
    icon: IconHeroiconsSolidMap,
  },
  // Only on a site with a storage compound (never on "All sites").
  ...(hasStorageCompound.value
    ? [
        {
          label: "Storage",
          to: { name: "storage" },
          icon: IconHeroiconsSolidArchiveBox,
        },
      ]
    : []),
  {
    label: "Reports",
    to: { name: "reports" },
    icon: IconHeroiconsSolidChartBar,
  },
  ...(isExperienceFreedom.value || hasKeysafePitch.value
    ? [
        {
          label: "Keycodes",
          to: { name: "keycodes" },
          icon: IconHeroiconsSolidFingerPrint,
        },
      ]
    : []),
]);

const secondaryRoutes = computed(() => [
  ...(isCompanion.value
    ? [
        {
          // "Content" distinguishes the editor from the Arrival Companion app
          // itself, which is the external link further down the same menu.
          label: "Companion Content",
          to: { name: "companionContent" },
          icon: IconHeroiconsSolidDeviceMobile,
        },
      ]
    : []),
]);
</script>
